import { useTraderText } from '../context/TraderLanguageContext';
/**
 * Trader Login Page
 * Dark theme login with email/password, matching ZrpGlobal style
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTraderAuth } from '../context/TraderAuthContext';
import { useTraderLanguage } from '../context/TraderLanguageContext';
import { useTraderTheme } from '../context/TraderThemeContext';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import {TenantBrand} from '../../branding/BrandingContext';

const TraderLogin = () => {
  const tx = useTraderText();
  const navigate = useNavigate();
  const { login } = useTraderAuth();
  const { t, language, toggleLanguage } = useTraderLanguage();
  const { isDark } = useTraderTheme();
  
  const [email, setEmail] = useState('trader@webtra.com');
  const [password, setPassword] = useState('Trader!2026');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error(tx(t('validation.required')));
      return;
    }
    
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    
    if (result.success) {
      toast.success(tx(t('common.success')));
      navigate('/trader');
    } else {
      toast.error(tx(result.error));
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center px-4 ${
      isDark ? 'bg-[#0d1421]' : 'bg-gray-100'
    }`}>
      {/* Language Toggle - Top Right */}
      <button
        onClick={toggleLanguage}
        data-testid="trader-login-language-toggle"
        aria-label={tx('Language')}
        className="fixed top-4 right-4 flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
      >
        <span className="text-2xl">{language === 'en' ? '🇬🇧' : '🇹🇷'}</span>
        <span className={isDark ? 'text-white' : 'text-gray-800'}>{tx(language.toUpperCase())}</span>
      </button>

      <div className="w-full max-w-md">
        <div className="tenant-login-brand"><TenantBrand id="trader-login-brand"/></div>
        {/* Login Form */}
        <div className="space-y-6">
          {/* Title */}
          <div>
            <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('auth.login')}
            </h1>
            <p className={`mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {t('auth.loginSubtitle')}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-700'}`}>
                {t('auth.email')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Mail size={20} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`w-full pl-12 pr-4 py-4 rounded-xl border focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                    isDark 
                      ? 'bg-[#1a2332] border-gray-700 text-white placeholder-gray-500' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                  }`}
                  placeholder={t('auth.email')}
                  data-testid="login-email-input"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-700'}`}>
                {t('auth.password')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock size={20} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`w-full pl-12 pr-12 py-4 rounded-xl border focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                    isDark 
                      ? 'bg-[#1a2332] border-gray-700 text-white placeholder-gray-500' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                  }`}
                  placeholder={t('auth.password')}
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center"
                >
                  {showPassword ? (
                    <EyeOff size={20} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
                  ) : (
                    <Eye size={20} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
                  )}
                </button>
              </div>
            </div>

            {/* Forgot Password */}
            <div className="text-right">
              <Link 
                to="/trader/forgot-password" 
                className="text-blue-400 hover:text-blue-300 text-sm font-medium"
              >
                {t('auth.forgotPassword')}
              </Link>
            </div>

            {/* Login Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="login-submit-btn"
            >
              {loading ? t('common.loading') : t('auth.loginButton')}
            </button>
          </form>

          {/* Sign Up Link */}
          <p className={`text-center ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {t('auth.noAccount')}{' '}
            <Link to="/trader/register" className="text-blue-400 hover:text-blue-300 font-semibold">
              {t('auth.signUp')}
            </Link>
          </p>

          {/* Risk Warning */}
          <p className={`text-center text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
            {t('auth.riskWarning')}{' '}
            <Link to="/trader/risk-disclosure" className="text-blue-400 hover:text-blue-300">
              {t('auth.learnMore')}
            </Link>
          </p>

          {/* reCAPTCHA Notice */}
          <p className={`text-center text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
            {t('auth.recaptchaText')}{' '}
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">
              {t('auth.privacyLink')}
            </a>
            {' '}{t('common.and')}{' '}
            <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">
              {t('auth.termsOfService')}
            </a>
            {' '}{t('auth.apply')}.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TraderLogin;
